import React, { useState, useEffect } from "react";
import { PRODUCT_API } from "../../../data/api";
import { CATEGORY_API } from "../../../data/api";

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [stockFilter, setStockFilter] = useState("");

  // Fetch Inventory
  const fetchInventory = async () => {
    try {
      const vendorId = Number(localStorage.getItem("vendorId"));
      const res = await fetch(PRODUCT_API.GET_BY_VENDOR(vendorId));
      if (!res.ok) throw new Error("Failed to fetch inventory");
      const data = await res.json();
      setInventory(data);
    } catch (err) {
      console.log("Error fetching inventory:", err);
      setInventory([]);
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(CATEGORY_API.GET_ALL); // ✅ Correct endpoint
      if (!res.ok) throw new Error("Failed to fetch categories");
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.log("Error fetching categories:", err);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchCategories();
  }, []);

  // Local quantity update
  const updateStockLocal = (id, change) => {
    setInventory((prev) =>
      prev.map((item) =>
        item.productId === id
          ? { ...item, quantity: Math.max(0, item.quantity + change) }
          : item
      )
    );
  };

  // Filtered Inventory
  const filteredInventory = inventory.filter((item) => {
    const matchName = item.productName
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchCategory = category ? item.categoryName === category : true;
    const matchStock =
      stockFilter === "out"
        ? item.quantity === 0
        : stockFilter === "low"
        ? item.quantity < 5
        : true;

    return matchName && matchCategory && matchStock;
  });

  return (
    <div className="p-5">
      <h1 className="text-3xl font-bold mb-4">📦 Inventory Management</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p className="text-gray-500">Total Products</p>
          <h2 className="text-3xl font-bold">{inventory.length}</h2>
        </div>

        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p className="text-gray-500">Low Stock</p>
          <h2 className="text-3xl font-bold">
            {inventory.filter((i) => i.quantity > 0 && i.quantity < 5).length}
          </h2>
        </div>

        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p className="text-gray-500">Out of Stock</p>
          <h2 className="text-3xl font-bold">
            {inventory.filter((i) => i.quantity === 0).length}
          </h2>
        </div>

        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p className="text-gray-500">Total Inventory Value</p>
          <h2 className="text-3xl font-bold">
            ₹
            {inventory
              .reduce((sum, i) => sum + i.price * i.quantity, 0)
              .toLocaleString()}
          </h2>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search Product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 rounded-lg w-full md:w-1/3"
        />

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="border p-2 rounded-lg"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.categoryName}>
              {cat.categoryName}
            </option>
          ))}
        </select>

        <select
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
          className="border p-2 rounded-lg"
        >
          <option value="">All Stock</option>
          <option value="low">Low Stock</option>
          <option value="out">Out of Stock</option>
        </select>
      </div>

      {/* Inventory Table */}
      <div className="bg-white p-4 rounded-xl shadow border overflow-x-auto">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-3">Product</th>
              <th className="p-3">Category</th>
              <th className="p-3">Stock</th>
              <th className="p-3">Status</th>
              <th className="p-3">Price</th>
              <th className="p-3">Total Value</th>
              <th className="p-3 text-center">Quantity Control</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-4 text-gray-500">
                  No products found...
                </td>
              </tr>
            ) : (
              filteredInventory.map((item) => {
                const status =
                  item.quantity === 0
                    ? { text: "Out of Stock", color: "red" }
                    : item.quantity < 5
                    ? { text: "Low Stock", color: "yellow" }
                    : { text: "In Stock", color: "green" };

                return (
                  <tr key={item.productId} className="border-b">
                    <td className="p-3 font-medium">{item.productName}</td>
                    <td className="p-3">{item.categoryName || "-"}</td>
                    <td className="p-3 font-semibold">{item.quantity}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded text-sm bg-${status.color}-100 text-${status.color}-600`}
                      >
                        {status.text}
                      </span>
                    </td>
                    <td className="p-3 font-semibold">₹{item.price}</td>
                    <td className="p-3 font-semibold">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </td>
                    <td className="p-3 flex justify-center gap-2">
                      <button
                        onClick={() =>
                          item.quantity > 0 &&
                          updateStockLocal(item.productId, -1)
                        }
                        className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600"
                      >
                        -
                      </button>

                      <button
                        onClick={() => updateStockLocal(item.productId, +1)}
                        className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Inventory;
